import { cn } from '@glade-ai/ui';
import axios from 'axios';
import { AnimatePresence, m } from 'motion/react';
import React, { type ChangeEventHandler, type ForwardRefRenderFunction, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import s from '@/components/DesignLibrary/Atoms/Input/Input.module.scss';
import Label from '@/components/DesignLibrary/Atoms/shared/Label';
import { ADDRESS_COMPLETION_API_KEY } from '@/configuration/client';
import { logError } from '@/providers/ErrorTracking';

import AdditionalStyles from './AddressUSAutocomplete.module.scss';

const DEBOUNCE_DELAY_MS = 350;

type AddressUSAutocomplete = {
  line1?: string;
  city?: string;
  state?: string;
  zip?: string;
};

type InputProps = {
  value: string;
  onAddressChange: (address: AddressUSAutocomplete | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  optional?: boolean;
};

type CustomSelectOption = {
  title: string;
  value: string;
};

type PlaceIdItem = { id: string; item: string };

interface AddressComponent {
  longText: string;
  shortText: string;
  types: string[];
  languageCode: string;
}

const AddressUSAutocomplete: ForwardRefRenderFunction<HTMLInputElement, InputProps> = ({
  value,
  disabled,
  optional,
  onAddressChange,
  onFocus,
  onBlur,
}) => {
  const [autocompleteOptions, setAutocompleteOptions] = useState<CustomSelectOption[]>([]);
  const [displayValue, setInputDisplayValue] = useState(value);
  const [placesId, setPlacesId] = useState<PlaceIdItem[] | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);
  const sessionTokenRef = useRef<string | null>(null);

  useEffect(() => {
    setInputDisplayValue(value);
  }, [value]);

  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const optionElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (optionElement) {
        const listRect = listRef.current.getBoundingClientRect();
        const optionRect = optionElement.getBoundingClientRect();
        if (optionRect.bottom > listRect.bottom) {
          listRef.current.scrollTop += optionRect.bottom - listRect.bottom;
        } else if (optionRect.top < listRect.top) {
          listRef.current.scrollTop -= listRect.top - optionRect.top;
        }
      }
    }
  }, [selectedIndex]);

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  const getAddressDetails = async (val: string): Promise<void> => {
    const structuredAddress = {
      city: '',
      country: '',
      line1: '',
      state: '',
      zip: '',
    };
    const place = placesId?.find((location: PlaceIdItem) => location.item === val);
    const placeId = place?.id;

    if (!placeId) {
      return;
    }

    try {
      const response = await axios.get(`https://places.googleapis.com/v1/places/${placeId}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': ADDRESS_COMPLETION_API_KEY,
          'X-Goog-FieldMask': 'addressComponents,formattedAddress',
        },
      });

      if (!response.data.addressComponents) {
        logError(new Error('No place details found'));
        return;
      }

      const addressComponents = response.data.addressComponents || [];
      addressComponents.forEach((component: AddressComponent) => {
        const { types } = component;
        if (types.includes('street_number') || types.includes('route')) {
          structuredAddress.line1 += `${component.longText} `;
        } else if (types.includes('locality') || (types.includes('sublocality_level_1') && !structuredAddress.city)) {
          structuredAddress.city = component.longText;
        } else if (types.includes('administrative_area_level_1')) {
          structuredAddress.state = component.shortText;
        } else if (types.includes('postal_code')) {
          structuredAddress.zip = component.longText;
        } else if (types.includes('country')) {
          structuredAddress.country = component.longText;
        }
      });

      const premiseComponent = addressComponents.find((component: AddressComponent) =>
        component.types.includes('premise'),
      );
      if (premiseComponent) {
        structuredAddress.line1 += `${structuredAddress.line1.endsWith(' ') || structuredAddress.line1 === '' ? '' : ' '}#${premiseComponent.longText}`;
      }

      structuredAddress.line1 = structuredAddress.line1.trim();
      setInputDisplayValue(structuredAddress.line1 || '');
      onAddressChange(structuredAddress);
    } catch (error) {
      logError(new Error('Error fetching address details: getAddressDetails'));
    }
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = async (event): Promise<void> => {
    const nextValue = event.target.value;
    setInputDisplayValue(nextValue);
    onAddressChange({ line1: nextValue });

    // clear pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (nextValue.length <= 3) {
      setIsOpen(false);
      setAutocompleteOptions([]);
      setPlacesId([]);
      setSelectedIndex(-1);
      sessionTokenRef.current = null; // end session when input is cleared/short
      return;
    }

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = uuidv4();
    }

    reqIdRef.current += 1;
    const myId = reqIdRef.current;
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const response = await axios.post(
          'https://places.googleapis.com/v1/places:autocomplete',
          {
            includedPrimaryTypes: ['street_address', 'premise'],
            includedRegionCodes: ['us'],
            input: nextValue,
            sessionToken: sessionTokenRef.current as string,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': ADDRESS_COMPLETION_API_KEY,
              'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
            },
          },
        );

        if (myId !== reqIdRef.current) {
          return; // stale response, ignore
        }

        const suggestions = Array.isArray(response.data?.suggestions) ? response.data.suggestions : [];
        if (!suggestions.length) {
          setIsOpen(false);
          setAutocompleteOptions([]);
          setPlacesId([]);
          setSelectedIndex(-1);
          return;
        }

        const mappedOptions = suggestions
          .map((place: { placePrediction?: { text?: { text?: string } } }) => {
            const title = place?.placePrediction?.text?.text;
            return title ? { title, value: title } : null;
          })
          .filter(Boolean) as CustomSelectOption[];

        const mappedPlaceIds = suggestions
          .map((place: { placePrediction?: { placeId?: string; text?: { text?: string } } }) => {
            const id = place?.placePrediction?.placeId;
            const item = place?.placePrediction?.text?.text;
            return id && item ? { id, item } : null;
          })
          .filter(Boolean) as PlaceIdItem[];

        setAutocompleteOptions(mappedOptions);
        setPlacesId(mappedPlaceIds);
        setIsOpen(mappedOptions.length > 0);
      } catch (_error) {
        if (myId !== reqIdRef.current) {
          return; // stale error, ignore
        }
        logError(new Error('Error fetching autocomplete suggestions: handleChange'));
        setIsOpen(false);
        setAutocompleteOptions([]);
        setPlacesId([]);
        setSelectedIndex(-1);
      }
    }, DEBOUNCE_DELAY_MS);
  };

  const handleSelectOption = (option: CustomSelectOption): void => {
    setIsOpen(false);
    setSelectedIndex(-1);
    getAddressDetails(option.value);
    inputRef.current?.focus();
    sessionTokenRef.current = null; // end session on selection
    onBlur?.();
  };

  const handleOptionMouseDown = (event: React.MouseEvent, option: CustomSelectOption): void => {
    event.preventDefault();
    handleSelectOption(option);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setIsOpen(true);
        setSelectedIndex((prevIndex) => (prevIndex + 1) % autocompleteOptions.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prevIndex) => (prevIndex - 1 + autocompleteOptions.length) % autocompleteOptions.length);
        break;
      case 'Enter':
        if (isOpen && selectedIndex > -1) {
          handleSelectOption(autocompleteOptions[selectedIndex]);
          event.preventDefault();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLUListElement>): void => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setTimeout(() => {
        setIsOpen(false);
      }, 100);
      onBlur?.();
      setSelectedIndex(-1);
      sessionTokenRef.current = null; // end session on blur
    }
  };

  return (
    <div className={cn(s['input-field-wrapper'], AdditionalStyles['address-autocomplete-wrapper'])}>
      <Label compact label='Street Address Line 1' required={!optional}>
        <input
          id='address-input'
          ref={inputRef}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={onFocus}
          className={cn(s['input-field'], 'focus-ring')}
          aria-autocomplete='list'
          aria-controls='address-suggestions'
          placeholder='Enter an address'
          autoComplete='off'
          disabled={disabled}
          required={!optional}
          type='text'
        />
      </Label>
      <AnimatePresence>
        {isOpen && autocompleteOptions.length > 0 && (
          <m.ul
            layout='size'
            ref={listRef}
            id='address-suggestions'
            role='listbox'
            className={AdditionalStyles['address-ul']}
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{ opacity: 0, transition: { duration: 0.1 }, y: 4 }}
          >
            {autocompleteOptions.map((option, index) => (
              <m.li
                key={index}
                layout
                tabIndex={-1}
                role='option'
                aria-selected={selectedIndex === index}
                onMouseDown={(e) => handleOptionMouseDown(e, option)}
                className={AdditionalStyles['address-li']}
              >
                {option.title}
              </m.li>
            ))}
          </m.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddressUSAutocomplete;
